import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { getPriceList, setPrice, setListingStatus, catalogueMeta, listPolicies, savePolicy, runPolicy } from "../../services/catalogueService";
import { errorToast, successToast } from "../Toasts/Toasts";

// The whole customer price list on one screen.
//
// Pricing through each product's own page works but hides the thing that
// matters: where the gaps are. Here every listing is a row per size, the
// price is editable in place, and anything unpriced or unapproved is visible
// at a glance - a listing cannot go in front of a society until it has a
// standard price, so an empty cell is a blocked product.

const inr = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

const PriceList = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [edit, setEdit] = useState({}); // variantId -> value being typed
  const [busy, setBusy] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policyForm, setPolicyForm] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadPolicies = useCallback(async () => {
    try { setPolicies(await listPolicies()); } catch (e) { /* none set yet is normal */ }
  }, []);
  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const load = useCallback(async () => {
    try { setRows(await getPriceList()); } catch (e) { errorToast("Could not load the price list"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { catalogueMeta().then((m) => setCategories(m.listingCategories || [])).catch(() => {}); }, []);

  const save = async (row, size) => {
    const value = edit[size.variantId];
    if (value === undefined || value === "") return;
    setBusy(true);
    try {
      const r = await setPrice(row.zohoProductId, { zohoVariantId: size.variantId, variantName: size.variantName, price: Number(value) });
      if (r?.success) { setEdit((p) => ({ ...p, [size.variantId]: undefined })); load(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  const approve = async (row) => {
    setBusy(true);
    try {
      const r = await setListingStatus(row.zohoProductId, row.status === "Approved" ? "Withdrawn" : "Approved");
      if (r?.success) { successToast(row.status === "Approved" ? "Withdrawn" : `${row.displayName} is live`); load(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  // What the rule would do, before anything is written.
  const previewPolicy = async (category) => {
    setBusy(true);
    try {
      const r = await runPolicy({ category, apply: false });
      if (r?.success) setPreview(r.data); else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  const applyPolicy = async (overwrite) => {
    setBusy(true);
    try {
      const r = await runPolicy({ category: preview.category, apply: true, overwrite });
      if (r?.success) { successToast(`${r.data.priced.length} price(s) set`); setPreview(null); load(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  const shown = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!filter || r.category === filter) &&
          (!q || r.displayName.toLowerCase().includes(q.toLowerCase())) &&
          (!onlyGaps || r.unpriced > 0 || !r.sizes.length || r.status !== "Approved")
      ),
    [rows, filter, q, onlyGaps]
  );

  const priced = rows.reduce((a, r) => a + r.sizes.filter((s) => s.standard !== null).length, 0);
  const gaps = rows.reduce((a, r) => a + r.unpriced, 0);
  const live = rows.filter((r) => r.status === "Approved").length;

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0">Customer price list</h6>
            <div className="small text-muted">
              {rows.length} listings · {live} live to customers · {priced} prices set{gaps ? ` · ${gaps} size(s) unpriced` : ""}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Form.Check type="switch" id="gaps" label="Needs attention" checked={onlyGaps} onChange={(e) => setOnlyGaps(e.target.checked)} className="small" />
            <Form.Control size="sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 170 }} />
            <Form.Select size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 190 }}>
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </div>
        </div>

        <div className="card mt-2"><div className="card-body py-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="small fw-semibold me-1">Pricing policy</span>
            {categories.map((c) => {
              const set = policies.find((x) => x.category === c);
              const count = rows.filter((r) => r.category === c).length;
              if (!count) return null;
              return (
                <span key={c} className="badge bg-light text-dark border d-inline-flex align-items-center gap-2 py-2">
                  {c}:
                  <b>{set ? (set.method === "multiple" ? `${set.value}× cost` : `cost + ₹${set.value}`) : "not set"}</b>
                  <Button size="sm" variant="link" className="p-0 small" onClick={() => setPolicyForm(set ? { ...set } : { category: c, method: "markup", value: "", minPrice: "", roundTo: 5 })}>
                    {set ? "change" : "set"}
                  </Button>
                  {set && <Button size="sm" variant="link" className="p-0 small" disabled={busy} onClick={() => previewPolicy(c)}>apply</Button>}
                </span>
              );
            })}
          </div>
          <div className="small text-muted mt-1">A rule prices every size in the category from its current vendor cost. Prices you set by hand are left alone unless you say otherwise.</div>
        </div></div>

        <div className="card mt-2"><div className="card-body p-0">
          <div className="table-responsive table-scroll"><table className="table table-sm mb-0 align-middle">
            <thead>
              <tr><th>Plant</th><th>Category</th><th>Size</th><th className="text-end" style={{ width: 130 }}>Price (₹)</th><th>Society rates</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {shown.map((row) =>
                (row.sizes.length ? row.sizes : [{ variantId: `${row.zohoProductId}-none`, variantName: null, standard: null, overrides: [] }]).map((size, i) => (
                  <tr key={size.variantId} className={size.standard === null ? "table-warning" : ""}>
                    <td>
                      {i === 0 && (
                        <>
                          <Link to={`/catalogue/products/${row.zohoProductId}`}>{row.displayName}</Link>
                          {!row.hasImage && <span className="badge bg-light text-dark ms-1">no photo</span>}
                        </>
                      )}
                    </td>
                    <td className="small text-muted">{i === 0 ? row.category || <span className="text-danger">not set</span> : ""}</td>
                    <td className="small">{size.variantName || <span className="text-muted">no size priced yet</span>}</td>
                    <td className="text-end">
                      {size.variantName && (
                        <Form.Control
                          size="sm"
                          type="number"
                          min="1"
                          step="0.5"
                          className="text-end"
                          disabled={busy}
                          value={edit[size.variantId] !== undefined ? edit[size.variantId] : inr(size.standard)}
                          onChange={(e) => setEdit((p) => ({ ...p, [size.variantId]: e.target.value }))}
                          onBlur={() => save(row, size)}
                          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                          placeholder="set"
                        />
                      )}
                    </td>
                    <td className="small text-muted">{i === 0 ? (row.sizes.flatMap((s) => s.overrides).length ? `${row.sizes.flatMap((s) => s.overrides).length} society rate(s)` : "") : ""}</td>
                    <td>{i === 0 && <span className={`badge bg-${row.status === "Approved" ? "success" : row.status === "Withdrawn" ? "secondary" : "warning text-dark"}`}>{row.status}</span>}</td>
                    <td className="text-end">
                      {i === 0 && (
                        <Button size="sm" variant={row.status === "Approved" ? "outline-secondary" : "success"} disabled={busy || (row.status !== "Approved" && row.unpriced === row.sizes.length)} onClick={() => approve(row)}>
                          {row.status === "Approved" ? "Withdraw" : "Approve"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
              {!shown.length && <tr><td colSpan={7} className="text-muted p-3">Nothing here. A product becomes a listing from its own page in the catalogue.</td></tr>}
            </tbody>
          </table></div>
        </div></div>
        <Modal show={!!policyForm} onHide={() => setPolicyForm(null)}>
          <Modal.Header closeButton><Modal.Title>Pricing policy — {policyForm?.category}</Modal.Title></Modal.Header>
          {policyForm && <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>How is this category priced?</Form.Label>
              <Form.Select value={policyForm.method} onChange={(e) => setPolicyForm({ ...policyForm, method: e.target.value })}>
                <option value="markup">Cost plus a fixed amount — how you price plants</option>
                <option value="multiple">A multiple of cost — how the gifting sheet prices pots</option>
              </Form.Select>
            </Form.Group>
            <div className="row g-2">
              <div className="col-4">
                <Form.Label className="small mb-0">{policyForm.method === "multiple" ? "Multiple" : "Add (₹)"}</Form.Label>
                <Form.Control type="number" min="0" step={policyForm.method === "multiple" ? "0.1" : "1"} value={policyForm.value} onChange={(e) => setPolicyForm({ ...policyForm, value: e.target.value })} autoFocus />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-0">Never below (₹)</Form.Label>
                <Form.Control type="number" min="0" value={policyForm.minPrice ?? ""} onChange={(e) => setPolicyForm({ ...policyForm, minPrice: e.target.value })} placeholder="optional" />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-0">Round up to</Form.Label>
                <Form.Control type="number" min="1" value={policyForm.roundTo} onChange={(e) => setPolicyForm({ ...policyForm, roundTo: e.target.value })} />
              </div>
            </div>
            <div className="small text-muted mt-2">
              Rounding is always upward. A floor matters on cheap items — a ₹2.50 pot at 2× is ₹5, which is not worth invoicing.
            </div>
          </Modal.Body>}
          <Modal.Footer>
            <Button variant="success" disabled={busy || !policyForm?.value} onClick={async () => {
              setBusy(true);
              try { const r = await savePolicy(policyForm); if (r?.success) { setPolicyForm(null); loadPolicies(); } else errorToast(r?.message); }
              catch (e) { errorToast(e?.message); } finally { setBusy(false); }
            }}>Save rule</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={!!preview} onHide={() => setPreview(null)} size="lg">
          <Modal.Header closeButton><Modal.Title>Apply to {preview?.category}</Modal.Title></Modal.Header>
          {preview && <Modal.Body>
            <div className="mb-2">
              <b>{preview.priced.length}</b> size(s) would be priced
              {preview.skipped.length > 0 && <span className="text-muted"> · {preview.skipped.length} already priced and left alone</span>}
            </div>
            <div style={{ maxHeight: 320, overflow: "auto" }}>
              <div className="table-responsive table-scroll"><table className="table table-sm">
                <thead><tr><th>Size</th><th className="text-end">Cost</th><th className="text-end">Price</th><th>From</th></tr></thead>
                <tbody>
                  {preview.priced.map((p) => (
                    <tr key={p.variantId}>
                      <td className="small">{p.name}</td>
                      <td className="text-end">₹{p.cost}</td>
                      <td className="text-end fw-semibold">₹{p.price}</td>
                      <td className="small text-muted">{p.vendor}, {p.costDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
            {!preview.priced.length && <div className="text-muted">Nothing to price — every size either has a price already or has no vendor cost recorded.</div>}
          </Modal.Body>}
          <Modal.Footer>
            {preview?.skipped.length > 0 && <Button variant="outline-danger" disabled={busy} onClick={() => applyPolicy(true)}>Overwrite existing too</Button>}
            <Button variant="success" disabled={busy || !preview?.priced.length} onClick={() => applyPolicy(false)}>Set these prices</Button>
          </Modal.Footer>
        </Modal>

        <div className="small text-muted mt-2 px-2">Type a price and press Enter or click away to save. A size with no price keeps the whole listing off the portal.</div>
      </div>
    </div>
  );
};

export default PriceList;
